import { useEffect, useRef } from "react";
import { laAttractions } from "@/mocks/laAttractions";

export default function LATourGuide() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("opacity-100", "translate-y-0");
          el.classList.remove("opacity-0", "translate-y-6");
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="tours"
      className="py-14 md:py-20 lg:py-24 px-4 md:px-6 lg:px-10 bg-background-50"
    >
      <div
        ref={ref}
        className="max-w-7xl mx-auto opacity-0 translate-y-6 transition-all duration-700 ease-out"
      >
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8 md:mb-12">
          <div>
            <span className="inline-block text-xs font-medium text-foreground-500 tracking-widest uppercase border border-foreground-200/60 px-3 py-1 rounded-full mb-4">
              LA Tour Guide
            </span>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-950 leading-tight">
              <span className="text-foreground-400">Discover</span>
              <br />
              <span className="italic text-foreground-950">
                Local Treasures
              </span>
            </h2>
          </div>
          <p className="text-foreground-600 text-sm max-w-md leading-relaxed lg:text-right">
            With Ece Czerw&rsquo;s local expertise, uncover LA&rsquo;s best
            photo spots, hidden caf&eacute;s, and iconic landmarks.
            Personalized tour itineraries that let you truly experience the soul
            of Los Angeles.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {laAttractions.map((attraction, idx) => (
            <div
              key={attraction.id}
              className="group rounded-2xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200/60 transition-all duration-300"
            >
              <div className="relative h-48 md:h-52 overflow-hidden">
                <img
                  src={attraction.image}
                  alt={attraction.name}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/5 transition-colors" />
              </div>
              <div className="p-4 md:p-5">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-2.5">
                  {attraction.category}
                </span>
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2">
                  {attraction.name}
                </h3>
                <p className="text-foreground-600 text-xs md:text-sm leading-relaxed">
                  {attraction.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}